import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import CampOpsNav from "@/components/CampOpsNav";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Radio, CheckCircle2, ArrowRight, Printer } from "lucide-react";
import { METRIC_LABELS, METRIC_UNITS } from "@/lib/metrics";

export default function CampStation() {
  const { campId } = useParams();
  const [camp, setCamp] = useState(null);
  const [stations, setStations] = useState([]);
  const [station, setStation] = useState(null);
  const [metricType, setMetricType] = useState(null);
  const [verification, setVerification] = useState("cbr_verified");
  const [num, setNum] = useState("");
  const [val, setVal] = useState("");
  const [saving, setSaving] = useState(false);
  const [log, setLog] = useState([]);
  const numRef = useRef(null);

  useEffect(() => {
    api.get(`/admin/camps/${campId}/roster`).then(({ data }) => {
      setCamp(data.camp);
      setStations(data.stations || []);
    }).catch(() => {});
  }, [campId]);

  const pickStation = (s) => {
    setStation(s);
    setMetricType(s.metrics[0]);
    setLog([]);
    setTimeout(() => numRef.current?.focus(), 50);
  };

  const save = async () => {
    if (num === "" || val === "") {
      toast.error("Enter jersey # and a result");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/camps/${campId}/quick-metric`, {
        metric_type: metricType,
        player_number: Number(num),
        value: Number(val),
        verification_status: verification,
      });
      const name = `${data.player.first_name} ${data.player.last_name}`;
      toast.success(`#${num} ${name}: ${data.value} ${data.units || ""}`);
      setLog((l) => [{ num, name, value: data.value, units: data.units, metric: metricType, t: new Date() }, ...l].slice(0, 12));
      setNum("");
      setVal("");
      numRef.current?.focus();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter") save(); };

  const printSheets = async () => {
    try {
      const params = station ? { station: station.key } : {};
      const res = await api.get(`/admin/camps/${campId}/station-sheets.pdf`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not generate sheets");
    }
  };

  return (
    <AdminLayout>
      <div className="p-8 max-w-5xl">
        <Link to="/admin/camps" className="label-overline text-slate-500 hover:text-[#E63946]">← All Camps</Link>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="label-overline text-[#E63946] mt-3">Station Mode</div>
            <h1 data-testid="station-title" className="font-display text-4xl uppercase tracking-tighter mt-2">{camp?.title || "Camp"}</h1>
          </div>
          <button
            data-testid="print-station-sheets"
            onClick={printSheets}
            className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:border-[#E63946] hover:text-[#E63946] px-4 py-2.5 text-xs font-semibold uppercase tracking-wider rounded-sm"
          >
            <Printer className="w-4 h-4" /> Print {station ? "This Sheet" : "Station Sheets"}
          </button>
        </div>

        <CampOpsNav campId={campId} />

        {!station ? (
          <div>
            <p className="text-sm text-slate-600 mb-4">Pick your station. Volunteers enter jersey number → result → Save. No profile navigation needed.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {stations.map((s) => (
                <button
                  key={s.key}
                  data-testid={`station-pick-${s.key}`}
                  onClick={() => pickStation(s)}
                  className="text-left bg-white border border-slate-200 hover:border-[#E63946] p-6 rounded-sm transition-colors"
                >
                  <Radio className="w-6 h-6 text-[#E63946]" />
                  <div className="font-display text-xl uppercase tracking-tight mt-3">{s.label}</div>
                  <div className="text-xs text-slate-500 mt-1">{s.metrics.map((m) => METRIC_LABELS[m]).join(" · ")}</div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Entry panel */}
            <div className="bg-[#0A192F] text-white p-6 rounded-sm">
              <div className="flex items-center justify-between">
                <div className="label-overline text-[#E63946]">{station.label}</div>
                <button data-testid="station-change" onClick={() => setStation(null)} className="text-xs uppercase tracking-wider text-white/60 hover:text-white">Change</button>
              </div>

              {station.metrics.length > 1 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {station.metrics.map((m) => (
                    <button
                      key={m}
                      data-testid={`station-metric-${m}`}
                      onClick={() => setMetricType(m)}
                      className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm ${metricType === m ? "bg-[#E63946] text-white" : "bg-white/10 text-white/70 hover:bg-white/20"}`}
                    >
                      {METRIC_LABELS[m]}
                    </button>
                  ))}
                </div>
              )}

              <div className="mt-6 grid grid-cols-2 gap-4">
                <div>
                  <label className="label-overline text-white/50">Jersey #</label>
                  <input
                    ref={numRef}
                    data-testid="station-number-input"
                    type="number"
                    value={num}
                    onChange={(e) => setNum(e.target.value)}
                    onKeyDown={onKey}
                    className="mt-1 w-full px-3 py-4 text-3xl font-data text-[#0A192F] rounded-sm"
                    placeholder="17"
                  />
                </div>
                <div>
                  <label className="label-overline text-white/50">{METRIC_LABELS[metricType]} ({METRIC_UNITS[metricType]})</label>
                  <input
                    data-testid="station-value-input"
                    type="number"
                    step="0.1"
                    value={val}
                    onChange={(e) => setVal(e.target.value)}
                    onKeyDown={onKey}
                    className="mt-1 w-full px-3 py-4 text-3xl font-data text-[#0A192F] rounded-sm"
                    placeholder="92"
                  />
                </div>
              </div>

              <button
                data-testid="station-save"
                onClick={save}
                disabled={saving}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#E63946] text-white px-6 py-4 text-lg font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60"
              >
                {saving ? "Saving…" : <>Save <ArrowRight className="w-5 h-5" /></>}
              </button>
              <p className="text-[11px] text-white/50 mt-2 text-center">Tip: press Enter to save and jump to the next player.</p>
            </div>

            {/* Recent log */}
            <div className="bg-white border border-slate-200 rounded-sm">
              <div className="px-5 py-3 border-b border-slate-200 font-display uppercase tracking-tight">This Session ({log.length})</div>
              <ul className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
                {log.length === 0 && <li className="p-5 text-sm text-slate-500">No entries yet. Saved results appear here.</li>}
                {log.map((e, i) => (
                  <li key={i} data-testid="station-log-item" className="px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      <span className="font-data">#{e.num}</span>
                      <span className="font-semibold">{e.name}</span>
                    </div>
                    <span className="font-data text-lg text-[#0A192F]">{e.value} <span className="text-xs text-slate-500">{e.units}</span></span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
